import React, { useState, useId } from 'react';
import { pocket } from '../api';
import './ThemeLabelPicker.css';

/**
 * "+ Add theme" control - lets a coach manually label a Pocket summary
 * with an existing theme (autocompleted from the coach's theme list) or a
 * brand new one, typed freeform. onLabel does the actual API call (it
 * differs for an unassigned note vs an already-assigned one) and should
 * throw/reject on failure.
 */
const ThemeLabelPicker = ({ coachId, existingThemes = [], onLabel, buttonLabel = '+ Add theme' }) => {
  const datalistId = useId();
  const [open, setOpen] = useState(false);
  const [allThemes, setAllThemes] = useState(null);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleOpen = async () => {
    setOpen(true);
    if (allThemes === null) {
      try {
        const res = await pocket.getAllThemes(coachId);
        setAllThemes(res.data?.themes || []);
      } catch (err) {
        console.error('❌ Error loading theme list:', err);
        setAllThemes([]);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setValue('');
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = value.trim();
    if (!name || saving) return;

    setSaving(true);
    setError(null);
    try {
      await onLabel(name);
      setValue('');
      setOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add theme');
    } finally {
      setSaving(false);
    }
  };

  const suggestions = (allThemes || []).filter(t => !existingThemes.includes(t.name));

  if (!open) {
    return (
      <button type="button" className="theme-label-toggle" onClick={handleOpen}>
        {buttonLabel}
      </button>
    );
  }

  return (
    <form className="theme-label-form" onSubmit={handleSubmit}>
      <input
        className="theme-label-input"
        list={datalistId}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Pick or type a theme..."
        autoFocus
        disabled={saving}
      />
      <datalist id={datalistId}>
        {suggestions.map(t => (
          <option key={t.id} value={t.name} />
        ))}
      </datalist>
      <button type="submit" className="theme-label-submit" disabled={saving || !value.trim()}>
        {saving ? '...' : 'Add'}
      </button>
      <button type="button" className="theme-label-cancel" onClick={handleClose} disabled={saving}>
        ✕
      </button>
      {error && <div className="theme-label-error">{error}</div>}
    </form>
  );
};

export default ThemeLabelPicker;
